import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from "vue"
import {
  MissionContractPayment,
  CreateMissionContractPaymentMutation,
  UpdateMissionContractPaymentMutation,
  CreateMissionContractPaymentMutationMutation,
  UpdateMissionContractPaymentMutationMutation,
  DeleteMissionContractDeliverableMutation,
  DeleteMissionContractDeliverableMutationMutation,
  DeleteMissionContractDeliverableMutationMutationVariables,
  UpdateMissionContractPaymentMutationMutationVariables,
  MissionLifecycleFragmentFragment,
} from "@/gql"
import { useUtilities } from "@/hooks/useUtilities"
import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import { RefetchQueryDescription } from "apollo-client/core/watchQueryOptions"
import { FetchResult } from "apollo-link"

export type MissionContractPaymentForm = {
  description: string
  amount: number | null
  dueDate: string | null
  deliverables: string[] | null
  name: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ContractMissionPaymentForm',
  props: {
    missionLifecycle: null,
    existingPayment: null,
    payments: null,
    refetchQueries: null
  },
  emits: ["save", "cancel"],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  missionLifecycle: MissionLifecycleFragmentFragment
  existingPayment?: MissionContractPayment | null
  payments?: MissionContractPayment[]
  refetchQueries: RefetchQueryDescription
}



const { truncate } = useUtilities()

const { mutate } = useMutation()
const { addSuccess, addGraphQLError, addMutationError } = useNotification()

const isSubmitting = ref(false)
const deliverables = ref<{ [key: string]: any }[]>([])
const mutatingDeliverable = ref<boolean[]>([])

const defaultForm: MissionContractPaymentForm = {
  description: "",
  amount: null,
  dueDate: null,
  deliverables: [],
  name: "",
}
const form = ref({ ...defaultForm })

const resetForm = () => {
  deliverables.value.length = 0
  form.value = { ...defaultForm }
  observer.value && (observer.value as any).reset()
}

const observer = ref(null)

const missionContractDeliverables = computed(() => {
  const deliverables = props.missionLifecycle.missionScope?.activities || []

  return deliverables.map((deliverable) => {
    return {
      id: deliverable.prn,
      name: truncate(deliverable.name || "Unnamed deliverable", 60),
    }
  })
})

const currency = computed(
  () => props.missionLifecycle.missionProposal?.missionPricingProposal?.currency?.code || "USD"
)

const onRemoveDeliverable = async (index: number) => {
  const deliverable = deliverables.value[index]

  if (deliverable.prn && !deliverable.missionScopeActivity) {
    try {
      mutatingDeliverable.value[index] = true

      const result = await mutate<
        DeleteMissionContractDeliverableMutationMutation,
        DeleteMissionContractDeliverableMutationMutationVariables
      >({
        mutation: DeleteMissionContractDeliverableMutation,
        variables: {
          id: deliverable.prn,
        },
      })

      if (result && result.data) {
        const error = result.data.deleteMissionContractDeliverable.error
        if (error) addMutationError(error)
        else deliverables.value.splice(index, 1)
      }
    } catch (e: any) {
      addGraphQLError(e)
    } finally {
      mutatingDeliverable.value[index] = false
    }
  } else {
    deliverables.value.splice(index, 1)
  }
}

const addPayment = async () => {
  isSubmitting.value = true

  const result = await mutate<CreateMissionContractPaymentMutationMutation>({
    mutation: CreateMissionContractPaymentMutation,
    variables: {
      missionLifecycleId: props.missionLifecycle.prn,
      ...form.value,
    },
    refetchQueries: props.refetchQueries,
    done: () => {
      isSubmitting.value = false
    },
  })

  if (result?.data?.createMissionContractPayment.missionContractPayment) {
    onAddPaymentComplete(result)
  }
}

const updatePayment = async () => {
  isSubmitting.value = true

  const result = await mutate<
    UpdateMissionContractPaymentMutationMutation,
    UpdateMissionContractPaymentMutationMutationVariables
  >({
    mutation: UpdateMissionContractPaymentMutation,
    variables: {
      missionContractPaymentId: props.existingPayment!.prn,
      description: form.value.description,
      amount: form.value.amount!,
      dueDate: form.value.dueDate,
      deliverables: form.value.deliverables || [],
      name: form.value.name,
    },
    refetchQueries: props.refetchQueries,
    done: () => {
      isSubmitting.value = false
    },
  })

  if (result?.data && !result.data.updateMissionContractPayment.error) {
    const data = {
      ...form.value,
    }

    resetForm()
    addSuccess("Payment updated successfully!")

    emit("save", data)
  }
}

const onAddPaymentComplete = (
  result: FetchResult<CreateMissionContractPaymentMutationMutation>
) => {
  if (result.data) {
    if (result.data.createMissionContractPayment.error) {
      addMutationError(result.data.createMissionContractPayment.error)
    } else {
      const data = {
        ...form.value,
      }

      resetForm()
      addSuccess("Payment added successfully!")

      return data
    }
  }
}

const populateForm = () => {
  if (props.existingPayment) {
    const payment: MissionContractPayment = { ...props.existingPayment }

    form.value.amount = payment.amount.amount
    form.value.description = payment.description || payment.name || ""
    form.value.dueDate = payment.dueDate
    form.value.name = payment.name || ""

    form.value.deliverables = payment.missionContractDeliverables
      .map((d) => d.missionScopeActivity?.prn)
      .filter((prn): prn is string => prn !== undefined)

    deliverables.value = [...payment.missionContractDeliverables]
  } else {
    resetForm()
  }
}

watch(() => props.existingPayment, populateForm, { immediate: true })

expose({ resetForm })

return { __sfc: true,props, truncate, emit, mutate, addSuccess, addGraphQLError, addMutationError, isSubmitting, deliverables, mutatingDeliverable, defaultForm, form, resetForm, observer, missionContractDeliverables, currency, onRemoveDeliverable, addPayment, updatePayment, onAddPaymentComplete, populateForm }
}

})